import {
  APP_BOOT_TS,
  isRunningInsideTelegram,
  setTelegramRequiredScrollLock
} from '../core/telegram.js';
import { apiAuthHeaders, getTelegramInitData } from '../core/api.js';
import type { HealthResponse } from '../types/api.js';

interface HealthResult {
  ok: boolean;
  maintenance: boolean;
  timedOut?: boolean;
}

interface MaintenanceWsMessage {
  type?: string;
  maintenance?: boolean;
}

(function initBootGate() {
  const overlay = document.getElementById('maintenance-overlay');
  const cardEl = document.getElementById('maintenance-card');
  const titleEl = document.getElementById('maintenance-title');
  const subtitleEl = document.getElementById('maintenance-subtitle');
  const appEl = document.querySelector('.app');

  const BOOT_MAX_MS = 5500;
  const HEALTH_TIMEOUT_MS = 4500;
  const TELEGRAM_REQUIRED_AFTER_MS = 15000;
  const MAINTENANCE_POLL_MS = 15000;
  const MAINTENANCE_POLL_OFFLINE_MS = 5000;

  let appRevealed = false;
  let maintenanceActive = false;
  let telegramRequiredActive = false;
  let statusWsConnected = false;
  let statusWs: WebSocket | null = null;
  let statusWsActive = false;
  let statusWsRetry = 0;
  let statusWsReconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function revealApp(): void {
    if (appRevealed) return;
    appRevealed = true;
    if (appEl) appEl.classList.remove('app-hidden-until-ready');
    if (!maintenanceActive && !telegramRequiredActive && overlay) {
      overlay.classList.add('maintenance-overlay-hidden');
    }
  }

  function setMaintenanceUi(on: boolean): void {
    maintenanceActive = !!on;
    if (!overlay) return;
    if (on) {
      overlay.classList.remove('maintenance-overlay-hidden');
      if (cardEl) cardEl.classList.add('maintenance-mode-active');
      if (titleEl) titleEl.textContent = 'Технические работы';
      if (subtitleEl) subtitleEl.textContent = 'Улучшаем сервис. Скоро вернёмся.';
    } else if (!telegramRequiredActive) {
      overlay.classList.add('maintenance-overlay-hidden');
      if (cardEl) cardEl.classList.remove('maintenance-mode-active');
    }
    if (!on) revealApp();
  }

  function setTelegramRequiredUi(on: boolean): void {
    telegramRequiredActive = !!on;
    if (!overlay) return;
    if (on) {
      overlay.classList.remove('maintenance-overlay-hidden');
      try {
        overlay.classList.add('telegram-required-mode');
      } catch (_) {}
      try {
        setTelegramRequiredScrollLock(true);
      } catch (_) {}
      if (cardEl) {
        cardEl.classList.add('telegram-required-mode');
        try {
          cardEl.classList.remove('maintenance-mode-active');
        } catch (_) {}
      }
      if (titleEl) titleEl.textContent = 'Сайт работает только внутри мини‑приложения';
      if (subtitleEl) {
        subtitleEl.textContent =
          'Для продолжения использования, пожалуйста, откройте приложение в Telegram.';
      }
    } else {
      try {
        overlay.classList.remove('telegram-required-mode');
      } catch (_) {}
      try {
        setTelegramRequiredScrollLock(false);
      } catch (_) {}
      if (cardEl) cardEl.classList.remove('telegram-required-mode');
      if (!maintenanceActive) overlay.classList.add('maintenance-overlay-hidden');
    }
  }

  function fetchHealth(): Promise<HealthResult> {
    return new Promise(function (resolve) {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutId = setTimeout(function () {
        try {
          if (controller) controller.abort();
        } catch (_) {}
        resolve({ ok: false, maintenance: false, timedOut: true });
      }, HEALTH_TIMEOUT_MS);

      fetch('/health', {
        cache: 'no-store',
        headers: apiAuthHeaders(),
        signal: controller ? controller.signal : undefined
      })
        .then(function (r) {
          return r
            .json()
            .then(function (data: HealthResponse | null) {
              clearTimeout(timeoutId);
              resolve({
                ok: true,
                maintenance: !!(data && data.maintenance)
              });
            })
            .catch(function () {
              clearTimeout(timeoutId);
              resolve({ ok: false, maintenance: false });
            });
        })
        .catch(function () {
          clearTimeout(timeoutId);
          resolve({ ok: false, maintenance: false });
        });
    });
  }

  function applyHealthResult(result: HealthResult): void {
    if (result && result.ok && result.maintenance) {
      setMaintenanceUi(true);
      return;
    }
    setMaintenanceUi(false);
  }

  function applyMaintenancePush(on: boolean): void {
    setMaintenanceUi(!!on);
  }

  function statusWsUrl(): string {
    const wsProto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return (
      wsProto +
      '//' +
      location.host +
      '/ws/app-status?initData=' +
      encodeURIComponent(getTelegramInitData() || '')
    );
  }

  function stopStatusWs(): void {
    statusWsActive = false;
    statusWsConnected = false;
    if (statusWsReconnectTimer) {
      clearTimeout(statusWsReconnectTimer);
      statusWsReconnectTimer = null;
    }
    if (statusWs) {
      try {
        statusWs.close();
      } catch (_) {}
      statusWs = null;
    }
  }

  function ensureStatusWs(): void {
    if (!statusWsActive) return;
    if (statusWs && statusWs.readyState === 1) return;
    if (statusWsReconnectTimer) {
      clearTimeout(statusWsReconnectTimer);
      statusWsReconnectTimer = null;
    }

    try {
      statusWs = new WebSocket(statusWsUrl());
    } catch (_) {
      statusWs = null;
      scheduleStatusWsReconnect();
      return;
    }

    statusWs.onopen = function () {
      statusWsRetry = 0;
      statusWsConnected = true;
    };

    statusWs.onmessage = function (ev: MessageEvent) {
      let msg: MaintenanceWsMessage | null = null;
      try {
        msg = JSON.parse(String(ev.data || '')) as MaintenanceWsMessage;
      } catch (_) {
        return;
      }
      if (!msg || msg.type !== 'maintenance') return;
      applyMaintenancePush(!!msg.maintenance);
    };

    statusWs.onclose = function () {
      statusWsConnected = false;
      statusWs = null;
      if (!statusWsActive) return;
      scheduleStatusWsReconnect();
      fetchHealth().then(applyHealthResult);
    };

    statusWs.onerror = function () {
      try {
        if (statusWs) statusWs.close();
      } catch (_) {}
    };
  }

  function scheduleStatusWsReconnect(): void {
    if (!statusWsActive || statusWsReconnectTimer) return;
    statusWsRetry += 1;
    const delay = Math.min(8000, 400 + statusWsRetry * 400);
    statusWsReconnectTimer = setTimeout(function () {
      statusWsReconnectTimer = null;
      ensureStatusWs();
    }, delay);
  }

  function startStatusWs(): void {
    if (window.location.protocol === 'file:') return;
    statusWsActive = true;
    ensureStatusWs();
  }

  function maybeTelegramRequired(): void {
    try {
      const host = window.location && window.location.host ? String(window.location.host) : '';
      if (host !== 'app.deepdesignpc.ru') return;
      if (isRunningInsideTelegram()) {
        if (telegramRequiredActive) setTelegramRequiredUi(false);
        return;
      }
      if (Date.now() - APP_BOOT_TS < TELEGRAM_REQUIRED_AFTER_MS) return;
      setTelegramRequiredUi(true);
    } catch (_) {}
  }

  function pollMaintenanceFallback(): void {
    if (window.location.protocol === 'file:') return;
    fetchHealth().then(applyHealthResult);
  }

  function startLoops(): void {
    setInterval(pollMaintenanceFallback, MAINTENANCE_POLL_MS);

    setInterval(function () {
      if (!statusWsConnected) pollMaintenanceFallback();
    }, MAINTENANCE_POLL_OFFLINE_MS);

    setInterval(maybeTelegramRequired, 2000);

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      pollMaintenanceFallback();
      maybeTelegramRequired();
      if (statusWsActive) ensureStatusWs();
    });

    window.addEventListener('beforeunload', stopStatusWs);
  }

  function boot(): void {
    if (window.location.protocol === 'file:') {
      revealApp();
      return;
    }

    let healthDone = false;
    let healthResult: HealthResult = { ok: false, maintenance: false };

    startStatusWs();

    fetchHealth().then(function (result) {
      healthDone = true;
      healthResult = result;
      applyHealthResult(result);
      revealApp();
    });

    setTimeout(function () {
      if (!appRevealed) {
        if (!healthDone) applyHealthResult(healthResult);
        revealApp();
      }
    }, BOOT_MAX_MS);

    startLoops();
    maybeTelegramRequired();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
